import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, Collapse, styled } from "~/common/mui";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Add, ExpandLess } from "@mui/icons-material";
import { ProductLineList } from "./ProductLineList";
import { type ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import type { LineItem, ResolvedGroupedCartEntry } from "~/features/cart/cart/models/CartData";
import { useDispatch } from "react-redux";
import { copyLastPackageEntry } from "../cart/redux/actions/cartPageActions";

interface CartEntryDetailsProps {
  initiallyExpanded?: boolean;
  packGroups: ResolvedGroupedCartEntry["packGroups"];
  product: ProductDescriptor;
  renderItem: (lineItem: LineItem, product: ProductDescriptor) => ReactNode;
  editMode: boolean;
}

export const CartEntryDetails = ({
  packGroups,
  product,
  initiallyExpanded,
  renderItem,
  editMode,
}: CartEntryDetailsProps) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(initiallyExpanded ?? false);

  const dispatch = useDispatch();
  const copyEntry = () => {
    const group = packGroups[0];
    return dispatch(copyLastPackageEntry(group.packEntryIds[group.packEntryIds.length - 1]));
  };
  return (
    <DetailsRoot $expanded={expanded}>
      <Box sx={{ p: 1, borderTop: 1, borderColor: "divider" }}>
        <Button
          variant="text"
          startIcon={expanded ? <ExpandLess /> : <ExpandMoreIcon />}
          color="textPrimary"
          onClick={() => setExpanded((prev) => !prev)}
        >
          {`${expanded ? t("common.hide") : t("common.show")} ${t("cartPage.summary.productLines")}`}
        </Button>
      </Box>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ p: 1 }}>
          <ProductLineList packGroups={packGroups} product={product} renderItem={renderItem} />
        </Box>
        {editMode && (
          <Box sx={{ p: 2 }}>
            <Button onClick={copyEntry} startIcon={<Add />} variant="outlined">
              {t("cartPage.table.copyRow")}
            </Button>
          </Box>
        )}
      </Collapse>
    </DetailsRoot>
  );
};

const DetailsRoot = styled(Box)<{ $expanded: boolean }>(({ theme, $expanded }) => ({
  background: $expanded ? theme.palette.surfaceContainerLow?.main : theme.palette.common.white,
  transition: `background ${theme.transitions.duration.standard}ms ${theme.transitions.easing.easeInOut}`,
}));
